// Slash commands — the typed way to invoke the chat's operations.
//
// This file is a REGISTRY AND A MATCHER, nothing else. It has no React, no DOM,
// no side effects, and above all NO RUN PATH: resolving "/daily-tasks" yields an
// operation id, and the caller runs it through exactly the same startOperation()
// the shortcut tiles call. A second way to start a run would be a second place
// for the RBAC gate and the in-flight lock to be forgotten.
//
// ONE SOURCE OF TRUTH. The commands are derived from OPERATIONS at module load,
// so an operation added to the catalog gets a command for free and one removed
// stops being typeable. The only thing that lives here is the stable NAME —
// the id ('initial-outreach-50') carries a count that would be a lie to type,
// so the name is mapped rather than slugified from it.
import { OPERATIONS } from './agentRun.js'

// operation id -> the name a person types. Stable: it is in a teammate's muscle
// memory and in the thread history, so it must not drift when a label is
// reworded or a target count changes.
const SLASH_NAMES = {
  'initial-outreach-50': 'initial-outreach',
  'followup-50': 'daily-tasks',
  'reply-open-threads': 'follow-up',
}

// Names people USED to type. Never runnable — findSlashCommand does not read
// this map, so "/reply" cannot start anything — but closestSlashCommand does,
// so muscle memory gets "did you mean /follow-up?" instead of a cold
// "unknown command". (reply-open-threads was /reply before it grew into the
// whole morning worklist.)
const LEGACY_NAMES = {
  reply: 'reply-open-threads',
}

// A name for an operation this file has never heard of, so a newly added
// operation is typeable the day it lands rather than silently missing from the
// palette. Trailing counts are dropped ('reach-out-25' -> 'reach-out'): a name
// carrying a number goes stale the moment the target does.
function slugFromId(id) {
  return (
    String(id || '')
      .toLowerCase()
      .replace(/[^a-z0-9-]+/g, '-')
      .replace(/-\d+$/, '')
      .replace(/^-+|-+$/g, '') || 'operation'
  )
}

/**
 * Every typeable command, in the catalog's own order.
 *
 * Each entry: { name, command, operationId, label, blurb }. `label` and `blurb`
 * are read off OPERATIONS rather than copied, so the palette shows exactly what
 * the shortcut tiles show.
 */
export const SLASH_COMMANDS = (() => {
  const used = new Set()
  return OPERATIONS.map((op) => {
    let name = SLASH_NAMES[op.id] || slugFromId(op.id)
    // Two operations may never share a name — the first listed keeps it and the
    // second falls back to its full id, because a command that resolves to
    // whichever operation happened to be listed first is worse than an ugly one.
    if (used.has(name)) name = slugFromId(op.id) === name ? String(op.id) : slugFromId(op.id)
    while (used.has(name)) name = `${name}-2`
    used.add(name)
    return {
      name,
      command: `/${name}`,
      operationId: op.id,
      label: op.label,
      blurb: op.blurb || '',
    }
  })
})()

// The ONLY shape that counts as a command attempt: the whole message is a single
// token that starts with a slash and contains nothing but name characters after
// it. That is deliberately narrow, so nothing a person actually writes gets
// hijacked —
//   "https://app.hubspot.com/…"  no slash at position 0
//   "/Users/jix/notes.txt"       a second slash
//   "and/or"                     slash is not at position 0
//   "2026/08/11"                 same
//   "/ the slash key"            whitespace after the slash
//   "/init now"                  whitespace — it is prose, so it is sent
// A message that merely CONTAINS a slash is always a message.
//
// (`$` in JavaScript matches end of input only, with no `m` flag — so "/init\n"
// does not sneak through.)
const SLASH_QUERY_RE = /^\/[a-zA-Z0-9-]*$/

/**
 * The typed name, lowercased, when `text` is a command attempt — otherwise null.
 * `/` alone yields '' (the palette's "show everything" state), which is why the
 * return is compared against null and never truthiness-tested.
 */
export function parseSlashQuery(text) {
  const raw = String(text ?? '')
  if (!SLASH_QUERY_RE.test(raw)) return null
  return raw.slice(1).toLowerCase()
}

// WHICH COMMANDS TAKE AN ARGUMENT, and what the argument is. Only outreach
// takes one today: "/initial-outreach illinois" scopes the run to a place.
// Everything else is argument-free and typing a word after it stays prose, so
// nothing a person writes gets hijacked by a command they did not mean.
export const COMMAND_ARGS = {
  'initial-outreach': {
    name: 'state',
    hint: 'a state — illinois, texas, CA…',
    optional: true,
    emptyLabel: 'Every state',
  },
}

/** The argument spec for a command name, or null when it takes none. */
export function commandArg(name) {
  return COMMAND_ARGS[String(name || '').replace(/^\//, '').toLowerCase()] || null
}

/**
 * A command attempt WITH its argument.
 *
 * Returns { name, arg, typingArg } or null for prose. The bar for treating
 * text-with-a-space as a command is deliberately high: the name before the
 * space must be a REAL command that actually takes an argument. So
 * "/initial-outreach illinois" is a command, while "/init now", "/ hello" and
 * "/daily-tasks tomorrow" all stay ordinary messages — exactly as they did
 * before arguments existed.
 */
export function parseSlashInput(text) {
  const raw = String(text ?? '')
  const withArg = raw.match(/^\/([a-zA-Z0-9-]+)[ \t]+(.*)$/)
  if (!withArg) {
    const name = parseSlashQuery(raw)
    return name === null ? null : { name, arg: '', typingArg: false }
  }
  const name = withArg[1].toLowerCase()
  if (!findSlashCommand(name) || !commandArg(name)) return null
  return { name, arg: withArg[2], typingArg: true }
}

/** Whether `text` should be treated as a command rather than a chat message. */
export function isSlashQuery(text) {
  return parseSlashQuery(text) !== null
}

/**
 * The commands matching a typed query, best first: prefix matches (what someone
 * typing "/init" means) ahead of anything that merely contains the string. An
 * empty query lists everything.
 */
export function matchSlashCommands(query) {
  const q = String(query || '').toLowerCase()
  if (!q) return [...SLASH_COMMANDS]
  const prefix = []
  const rest = []
  for (const cmd of SLASH_COMMANDS) {
    if (cmd.name.startsWith(q)) prefix.push(cmd)
    else if (cmd.name.includes(q) || cmd.label.toLowerCase().includes(q)) rest.push(cmd)
  }
  return [...prefix, ...rest]
}

/** Exact command by name ('daily-tasks' or '/daily-tasks'), or null. */
export function findSlashCommand(name) {
  const n = String(name || '')
    .replace(/^\//, '')
    .toLowerCase()
  return SLASH_COMMANDS.find((cmd) => cmd.name === n) || null
}

// Levenshtein, small and iterative. Used only to answer "did you mean?", never
// to decide what to run — a near miss is offered, never executed.
function distance(a, b) {
  const rows = a.length + 1
  const cols = b.length + 1
  let prev = Array.from({ length: cols }, (_, j) => j)
  for (let i = 1; i < rows; i += 1) {
    const row = [i]
    for (let j = 1; j < cols; j += 1) {
      row[j] = Math.min(
        prev[j] + 1,
        row[j - 1] + 1,
        prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1),
      )
    }
    prev = row
  }
  return prev[cols - 1]
}

/**
 * The closest command to a name that matched nothing — for "did you mean?".
 * Returns null when nothing is close enough to be worth suggesting, so a wild
 * typo gets an honest "unknown" rather than a confident wrong guess.
 */
export function closestSlashCommand(name) {
  const n = String(name || '')
    .replace(/^\//, '')
    .toLowerCase()
  if (!n) return null
  // A retired name outranks any edit-distance guess: someone typing "/reply"
  // means the operation that used to answer to it, not whichever current name
  // happens to share letters with it.
  if (LEGACY_NAMES[n]) {
    const legacy = SLASH_COMMANDS.find((cmd) => cmd.operationId === LEGACY_NAMES[n])
    if (legacy) return legacy
  }
  let best = null
  let bestScore = Infinity
  for (const cmd of SLASH_COMMANDS) {
    // A substring hit is as close as it gets — "/tasks" should offer
    // /daily-tasks before any edit-distance arithmetic gets a say.
    const d = cmd.name.includes(n) || n.includes(cmd.name) ? 0 : distance(n, cmd.name)
    if (d < bestScore) {
      bestScore = d
      best = cmd
    }
  }
  // Beyond half the typed length the "suggestion" is noise.
  return bestScore <= Math.max(2, Math.ceil(n.length / 2)) ? best : null
}
